import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { tracker } from '@visikon/tracker/src';
import { SetDocumentTitle } from '../../internationalization/AppText';
import { updateVideoProgress } from '../../actions/userActions';
import { capitalizeFirstLetter, getVideoInfo } from '../../common/utils';
import { MediaFileUtils } from '@visikon/utils';
import { getTranslation } from '@visikon/core-models/typeUtils';
import * as Styles from './PlayerContainer.styles';
import { exitFullscreen, useVideoWatchedPercentage } from './videoUtils';
import { useId, useIsPrivacyPolicyShowing, useLanguage, useWholeContent } from '../../common/selectors';
import { calculatePercentage } from '../../common/numbers';
import { isFullScreenAction, Video } from '../../reducers/resourcesReducer';
import { useLocation } from 'react-router-dom';
import { useBackbone } from '@visikon/backbone/src';
import { EnvironmentName } from '@visikon/backbone/src/env';
import { useEffect } from 'react';
import videoPlaceHolder from '../../images/videoPlaceholder.png';
import VideoThumbnail from '../../components/VideoThumbnail';
import { setPrivacyPolicyAcceptedEvent } from '../../actions/resourcesActions';
import { useScreenOrientationEvent } from './ScreenOrientation.util';
import { GenericVideoPlayer, VideoPlayerProps } from './players/VideoPlayer.types';
import { LanguageCode } from '@visikon/core-models/content';
import HTMLVideoPlayer from './players/HTMlVideoPlayer';
import VidStackPlayer from './players/VidStackPlayer';
import { ErrorBoundary } from '../../common/ErrorBoundary';
import { FeatureSwitch } from '../../common/FeatureFlags';
import { logger } from '@visikon/utils/src/logger';
import { StatusNotice } from '../program/StatusNotice';

export const VIDEO_PLAYER_STR = '/videoplayer/';
export const VIDEO_PLAYER_ROUTE = `${VIDEO_PLAYER_STR}:id`;
const AVAILABLE_VIDEO_PLAYERS = {
  html5: HTMLVideoPlayer,
  vidstack: VidStackPlayer,
} as const;

export const useIsVideoPlayerOpen = () => {
  const location = useLocation();
  return location.pathname.indexOf(VIDEO_PLAYER_STR) !== -1;
};

interface IPropsFromState {
  videoInfo?: Video;
}

const CPlayerContainer = ({ videoInfo }: IPropsFromState) => {
  const dispatch = useDispatch();
  const language = useLanguage();
  const videoPlayerRef = React.useRef<GenericVideoPlayer>();
  const [loop, setLoop] = React.useState(false);
  const isPrivacyPolicyShowing = useIsPrivacyPolicyShowing();
  const videoId = videoInfo ? videoInfo._id : '';
  const watchedPercentage = useVideoWatchedPercentage(videoId);
  const { env, hosts } = useBackbone((state) => ({
    env: state.env,
    hosts: state.hosts,
  }));

  function UpdateLocalVideoProgress({ currentTime, duration }: GenericVideoPlayer) {
    const percentage = calculatePercentage(currentTime, duration);

    if (!Number.isNaN(percentage)) {
      dispatch(updateVideoProgress({ id: videoId, percentage }));
    }
  }

  useScreenOrientationEvent((type) => {
    console.log('ScreeOrientation changed! New Orientation', type, type.startsWith('landscape'));

    if (type.startsWith('landscape')) {
      videoPlayerRef?.current?.enterFullscreen().catch((err) => console.warn('Failed to enter fullscreen aftr ScreenOrientation changed', err));
    }
  });

  const onFullscreenChange = (isFullscreen: boolean) => {
    tracker.trackEvent('Video', isFullscreen ? 'videoFullscreenEnter' : 'videoFullscreenExit');
  };

  // Code to handle the VIDEO_FULLSCREEN alias action
  const fullScreenAction = useSelector(isFullScreenAction(videoId));
  useEffect(() => {
    if (isPrivacyPolicyShowing && fullScreenAction && setPrivacyPolicyAcceptedEvent) {
      dispatch(
        setPrivacyPolicyAcceptedEvent(() => {
          setLoop(true);
          setTimeout(() => {
            videoPlayerRef?.current?.enterFullscreen();
          }, 300);
        }),
      );
    }
  }, [isPrivacyPolicyShowing, videoPlayerRef]);

  useEffect(() => {
    // Need to keep the video element in variable
    // Because unmounting is asynchronously and the reference may have been set to null
    // When executing the unmounting callback
    // See https://legacy.reactjs.org/blog/2020/08/10/react-v17-rc.html#potential-issues
    tracker.trackScreen('VideoScreen');
    const videoPlayer = videoPlayerRef.current;

    return () => {
      if (videoPlayer) {
        UpdateLocalVideoProgress(videoPlayer);
        tracker.trackEvent('Video', 'leave');
      }
    };
  }, []);

  const trackPlayerEvent = (evt: string) => {
    const videoPlayer = videoPlayerRef.current;
    let currentTime = videoPlayer?.currentTime ?? 0;
    const duration = videoPlayer?.duration ?? 0;

    const percentage = duration ? Math.round((currentTime / duration) * 100) : 0;
    const eventString = `video${capitalizeFirstLetter(evt)}`;
    let progressString = `percent-${percentage}`;

    if (evt === 'start') {
      const info = videoInfo!;
      progressString = `${info._id}-${info.trackingKey}`;
    }
    tracker.trackEvent('Video', eventString, progressString);
  };

  const pauseEvent = (player: GenericVideoPlayer) => {
    // if (ev.target.seeking) return;

    UpdateLocalVideoProgress(player);
  };

  const endEvent = () => {
    exitFullscreen();
    trackPlayerEvent('finished');
  };

  const onVideoLoaded = (video: GenericVideoPlayer) => {
    video.currentTime = (video.duration / 100) * watchedPercentage;

    const videoElement = video.videoElement;
    if (videoElement) {
      // matomoTitle gets transformed to 'datamato-title' in the DOM
      videoElement.dataset.matomoTitle = videoId;
      tracker.trackMediaElement(videoElement);
    } else {
      logger.warn('Failed to attach tracking to video element, videoElement is undefined');
    }
  };

  const videoSourced = videoInfo && getTranslation(videoInfo, language);
  if (!videoSourced) {
    return null;
  }

  let videoUrl = `${hosts.blobs}${videoSourced.src}`;
  if (env === EnvironmentName.Local) {
    videoUrl = `${hosts.core}media/video/file/${videoInfo._id}/${videoSourced.language}?modified=${videoSourced.lastModified}`;
  }

  if (!videoInfo && videoId !== 'undefined') {
    return (
      <h5>
        Loading...
        <pre>{JSON.stringify(videoId)}</pre>
      </h5>
    );
  }

  const thumbSrc = MediaFileUtils.getUriForThumbnail(videoInfo, language) || videoPlaceHolder;
  const videoPlayerProps: VideoPlayerProps = {
    videoRef: videoPlayerRef,
    title: videoInfo.name,
    src: videoUrl,
    loop: loop,
    onEnded: endEvent,
    onPause: pauseEvent,
    onVideoLoaded: onVideoLoaded,
    onDurationChange: pauseEvent,
    onFullscreenChange: onFullscreenChange,
    subtitles: videoSourced.subtitles?.map((sub) => ({
      // TODO: Properly type sub.key as LanguageCode in Core Models
      label: sub.label,
      languageCode: sub.key as LanguageCode,
      src: MediaFileUtils.getUriForSubtitles(videoInfo, language, sub.key),
    })),
  };

  return (
    <VideoPlayerContainer videoName={videoInfo.name}>
      <StatusNotice />
      {isPrivacyPolicyShowing ? (
        <VideoThumbnail thumbUrl={thumbSrc} spinner={true} />
      ) : (
        <ErrorBoundary fallback={<HTMLVideoPlayer {...videoPlayerProps} />}>
          <React.Suspense fallback={<VideoThumbnail thumbUrl={thumbSrc} spinner={true} />}>
            <FeatureSwitch
              components={AVAILABLE_VIDEO_PLAYERS}
              featureName="videoPlayer"
              props={videoPlayerProps}
              defaultComponent={HTMLVideoPlayer}
            />
          </React.Suspense>
        </ErrorBoundary>
      )}
    </VideoPlayerContainer>
  );
};

interface VideoPlayerContainerProps {
  videoName: string;
}

function VideoPlayerContainer({ videoName, children }: React.PropsWithChildren<VideoPlayerContainerProps>) {
  return (
    <Styles.FlexWrapper>
      <Styles.ContentContainer>
        <SetDocumentTitle text="" textNoTranslation={videoName} />
        <Styles.VideoContainer>{children}</Styles.VideoContainer>

        <Styles.VideoTitle>{videoName}</Styles.VideoTitle>
      </Styles.ContentContainer>
    </Styles.FlexWrapper>
  );
}

export function PlayerContainer() {
  const id = useId();
  const content = useWholeContent();
  const video = getVideoInfo(id, content);
  const props: IPropsFromState = {
    videoInfo: video,
  };

  return <CPlayerContainer {...props} />;
}
